import { Box, Button } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useEffect, useState } from "react";

type LayoutType = "default" | "shift" | "numbers";

type KeyboardLayout = {
  [K in LayoutType]?: string[];
};

type KeyboardDisplay = {
  [key: string]: string;
};

const KeyboardContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1.5),
  padding: theme.spacing(3),
  backgroundColor: theme.palette.charges.backgrounds.lighter,
  borderRadius: theme.spacing(2),
}));

const KeyboardRow = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1.5),
  justifyContent: "center",
}));

const KeyButton = styled(Button)(({ theme }) => ({
  minWidth: 100,
  minHeight: 100,
  fontSize: "32px",
  fontWeight: 600,
  textTransform: "none",
  backgroundColor: "white",
  color: theme.palette.text.primary,
  border: "none",
  borderRadius: theme.spacing(2),
  boxShadow: "0 2px 5px rgba(0,0,0,0.08)",
  "&:hover": {
    backgroundColor: "white",
    boxShadow: "0 4px 8px rgba(0,0,0,0.12)",
  },
  "&:active": {
    backgroundColor: theme.palette.charges.borders.light,
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
  },
}));

const SpecialKeyButton = styled(KeyButton)(({ theme }) => ({
  minWidth: 120,
  fontSize: "32px",
  "&:active": {
    backgroundColor: theme.palette.grey[400],
  },
}));

const SpaceKeyButton = styled(SpecialKeyButton)(() => ({
  minWidth: 350,
}));

export const CustomKeyboard = ({
  layoutName = "default",
  value,
  onChange,
  onKeyPress = (button: string) => {},
  layout = {
    default: [
      "q w e r t y u i o p",
      "a s d f g h j k l",
      "z x c v b n m",
      "{shift} {numbers} {space} {lock} {bksp}",
    ],
    shift: [
      "Q W E R T Y U I O P",
      "A S D F G H J K L",
      "Z X C V B N M",
      "{shift} {numbers} {space} {lock} {bksp}",
    ],
    numbers: [
      "1 2 3 4 5 6 7 8 9 0",
      "@ # $ & * ( ) _ - +",
      "! ? ' \" : ; / , .",
      "{abc} {space} {bksp}",
    ],
  },
  display = {
    "{bksp}": "⌫",
    "{enter}": "⏎",
    "{shift}": "⇧",
    "{space}": "Space",
    "{lock}": "Caps",
    "{numbers}": "?123",
    "{abc}": "ABC",
  },
}: {
  layoutName?: LayoutType;
  value: string;
  onChange: (input: string) => void;
  onKeyPress?: (button: string) => void;
  layout?: KeyboardLayout;
  display?: KeyboardDisplay;
}) => {
  const [currentLayout, setCurrentLayout] = useState<LayoutType>(layoutName);
  const [isCapsLocked, setIsCapsLocked] = useState(false);

  const handleKeyPress = (key: string) => {
    let newInput = value;

    if (key === "{bksp}") {
      newInput = value.slice(0, -1);
    } else if (key === "{space}") {
      newInput = value + " ";
    } else if (key === "{shift}") {
      if (!isCapsLocked && currentLayout === "default") {
        setCurrentLayout("shift");
      }
      onKeyPress(key);
      return;
    } else if (key === "{lock}") {
      setIsCapsLocked(!isCapsLocked);
      setCurrentLayout(isCapsLocked ? "default" : "shift");
      onKeyPress(key);
      return;
    } else if (key === "{numbers}") {
      setCurrentLayout("numbers");
      onKeyPress(key);
      return;
    } else if (key === "{abc}") {
      setCurrentLayout(isCapsLocked ? "shift" : "default");
      onKeyPress(key);
      return;
    } else if (key === "{enter}") {
      onKeyPress(key);
      return;
    } else {
      newInput = value + key;
    }

    onChange(newInput);
    onKeyPress(key);

    // Auto-switch from shift to default after typing a character (only if caps lock is not on)
    if (currentLayout === "shift" && !key.startsWith("{") && !isCapsLocked) {
      setCurrentLayout("default");
    }
  };

  const getKeyDisplay = (key: string): string => {
    return display[key] || key;
  };

  const isSpecialKey = (key: string): boolean => {
    return key.startsWith("{") && key.endsWith("}");
  };

  const renderRow = (row: string, rowIndex: number) => {
    const keys = row.split(" ");
    return (
      <KeyboardRow key={rowIndex}>
        {keys.map((key, keyIndex) => {
          const displayKey = getKeyDisplay(key);
          const isSpecial = isSpecialKey(key);
          const isSpace = key === "{space}";

          const ButtonComponent = isSpace
            ? SpaceKeyButton
            : isSpecial
              ? SpecialKeyButton
              : KeyButton;

          return (
            <ButtonComponent
              key={`${rowIndex}-${keyIndex}`}
              onClick={() => handleKeyPress(key)}
              variant="contained"
            >
              {displayKey}
            </ButtonComponent>
          );
        })}
      </KeyboardRow>
    );
  };

  const currentLayoutRows = layout[currentLayout] || layout.default || [];

  useEffect(() => {
    setCurrentLayout(layoutName);
  }, [layoutName]);

  return (
    <KeyboardContainer>
      {currentLayoutRows.map((row: string, index: number) =>
        renderRow(row, index),
      )}
    </KeyboardContainer>
  );
};
